import type {
  AnalyticsInitConfig,
  ListenerEntry,
  OnErrorContext,
  RemoteAnalyticsConfig,
  RemoteListenerEntry,
} from './types/config.js';
import { PreInitBuffer } from './buffer/pre-init-buffer.js';
import { ListenerRegistry } from './dispatcher/registry.js';
import { dispatchTrack, dispatchIdentify } from './dispatcher/dispatcher.js';
import type { DispatcherDeps } from './dispatcher/dispatcher.js';
import {
  createEnricherState,
  setUserId,
  clearUserId,
  type EnricherState,
} from './enricher/enricher.js';
import { buildListenersFromRemoteConfig } from './listeners/registry.js';
import { validateConfig } from './validate-config.js';
import { SDK_VERSION } from './version.js';

let enricherState: EnricherState | null = null;
let registry: ListenerRegistry | null = null;
/** Pre-init buffer. Lazy-created on first track/identify (queue mode, defaults); replaced at init() with user options, then flushed and set null after init. */
let preInitBuffer: PreInitBuffer | null = null;
let onError: ((error: Error, context?: OnErrorContext) => void) | undefined;
let initialized = false;

/** Default buffer before init: queue mode so events before init can be replayed. */
function getOrCreatePreInitBuffer(): PreInitBuffer {
  if (preInitBuffer == null) {
    preInitBuffer = new PreInitBuffer({
      mode: 'queue',
      maxSize: 100,
      ttlMs: 5000,
    });
  }
  return preInitBuffer;
}

function getEnricherState(): EnricherState {
  if (enricherState == null) {
    throw new Error('Analytics not initialized.');
  }
  return enricherState;
}

async function fetchRemoteConfig(options: AnalyticsInitConfig): Promise<RemoteAnalyticsConfig | null> {
  if (options.getConfig) {
    return options.getConfig();
  }
  if (options.configUrl) {
    const fetchFn = options.fetch ?? globalThis.fetch;
    const res = await fetchFn(options.configUrl);
    if (!res.ok) {
      throw new Error(`Analytics config fetch failed: ${res.status} ${res.statusText}`);
    }
    const json = await res.json();
    if (json == null || typeof json !== 'object') {
      throw new Error('Analytics config response must be a JSON object.');
    }
    return json as RemoteAnalyticsConfig;
  }
  return null;
}

function isInlineRemoteShape(listeners: unknown): listeners is RemoteListenerEntry[] {
  if (!Array.isArray(listeners) || listeners.length === 0) return false;
  return listeners.every(
    (entry): entry is RemoteListenerEntry =>
      entry != null &&
      typeof entry === 'object' &&
      typeof (entry as RemoteListenerEntry).id === 'string' &&
      'config' in entry &&
      !('listener' in entry && (entry as ListenerEntry).listener != null)
  );
}

/**
 * Initialize the SDK. Idempotent; calling again re-applies config.
 * Pre-queued events (if queue mode) are flushed after listeners are ready.
 * When configUrl or getConfig is set, SDK fetches config and auto-configures listeners from the built-in registry (or listenerFactories).
 */
export async function init(options: AnalyticsInitConfig): Promise<void> {
  validateConfig(options);

  let appId: string;
  let environment: string;
  let listeners: ListenerEntry[];

  const remote = await fetchRemoteConfig(options);
  if (remote != null) {
    appId = (options.appId ?? '').toString().trim();
    environment = options.environment ?? 'production';
    if (!appId) {
      throw new Error('Analytics init failed: appId is required (set in init options, e.g. from environment variables).');
    }
    listeners = buildListenersFromRemoteConfig(remote, {
      factories: options.listenerFactories,
      onError: options.onError,
    });
  } else {
    appId = options.appId.trim();
    environment = options.environment ?? 'production';
    const rawListeners = options.listeners ?? [];
    if (isInlineRemoteShape(rawListeners)) {
      listeners = buildListenersFromRemoteConfig(
        { listeners: rawListeners },
        {
          factories: options.listenerFactories,
          onError: options.onError,
        }
      );
    } else {
      listeners = rawListeners as ListenerEntry[];
    }
  }

  const preInitBehavior = options.preInitBehavior ?? 'drop';
  const queueMaxSize = options.queueMaxSize ?? 100;
  const queueTtlMs = options.queueTtlMs ?? 5000;
  onError = options.onError;

  const mode = preInitBehavior === 'queue' ? 'queue' : 'drop';
  preInitBuffer = new PreInitBuffer({ mode, maxSize: queueMaxSize, ttlMs: queueTtlMs });

  enricherState = createEnricherState(appId, environment, SDK_VERSION, options.sessionId);
  registry = new ListenerRegistry();

  for (const entry of listeners) {
    if (entry.enabled === false) continue;
    try {
      const initResult = entry.listener.init(entry.config);
      if (initResult instanceof Promise) {
        await initResult;
      }
      registry.add(entry.id, entry.listener);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      onError?.(error, { listenerId: entry.id });
    }
  }

  const deps: DispatcherDeps = {
    registry,
    getEnricherState,
    onError,
  };

  if (preInitBuffer) {
    if (preInitBehavior === 'queue') {
      preInitBuffer.flush(
        (payload) => dispatchTrack(deps, payload),
        (userId, traits) => {
          setUserId(enricherState!, userId);
          dispatchIdentify(deps, userId, traits);
        }
      );
    } else {
      preInitBuffer.flush(() => {}, () => {});
    }
    preInitBuffer = null;
  }

  initialized = true;
}

/**
 * Track an event. Fire-and-forget. Safe before init (queued or dropped per config).
 */
export function track(event: { name: string; properties?: Record<string, unknown>; timestamp?: string }): void {
  if (!initialized) {
    getOrCreatePreInitBuffer().pushTrack(event.name, event.properties, event.timestamp);
    return;
  }
  const deps: DispatcherDeps = {
    registry: registry!,
    getEnricherState,
    onError,
  };
  dispatchTrack(deps, event);
}

/**
 * Identify the user. Fire-and-forget. Safe before init (queued or dropped per config).
 */
export function identify(userId: string, traits?: Record<string, unknown>): void {
  if (!initialized) {
    getOrCreatePreInitBuffer().pushIdentify(userId, traits);
    return;
  }
  if (enricherState) setUserId(enricherState, userId);
  const deps: DispatcherDeps = {
    registry: registry!,
    getEnricherState,
    onError,
  };
  dispatchIdentify(deps, userId, traits);
}

/**
 * Flush listeners that batch (e.g. before page unload).
 */
export async function flush(): Promise<void> {
  if (!initialized || registry == null) return;
  const promises: Promise<void>[] = [];
  for (const { listener } of registry.getAll()) {
    if (typeof listener.flush === 'function') {
      const result = listener.flush();
      if (result instanceof Promise) promises.push(result);
    }
  }
  await Promise.all(promises);
}

/**
 * Clear queue and teardown listeners. Useful in tests or when identity changes.
 */
export async function reset(): Promise<void> {
  if (preInitBuffer) {
    preInitBuffer.flush(() => {}, () => {});
  }
  if (enricherState) clearUserId(enricherState);
  if (registry) {
    for (const { listener } of registry.getAll()) {
      if (typeof listener.teardown === 'function') {
        try {
          const result = listener.teardown();
          if (result instanceof Promise) await result;
        } catch (err) {
          const error = err instanceof Error ? err : new Error(String(err));
          onError?.(error, {});
        }
      }
    }
    registry.clear();
  }
  preInitBuffer = null;
  enricherState = null;
  registry = null;
  onError = undefined;
  initialized = false;
}

export function isInitialized(): boolean {
  return initialized;
}
